import { Component, Input, OnInit, Injector, Inject, NgZone } from '@angular/core';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { PractitionerOrganizationAffiliationService } from 'app/core/http/services/practititoner-organization-affiliation.service';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, FormArray } from '@angular/forms';
import { DiaHorario } from 'app/core/interfaces/practitionerHours';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogData } from 'app/core/interfaces/dialog';
import { OrganizationLocationService } from 'app/core/http/services/organization-location.service';
import { HoursOfOperation } from 'app/core/interfaces/datatypes-fhir';
import * as moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { isAtLeastOneDayFilled, validatePractitionerHours } from 'app/core/utils/hoursOfOperation-utils';

@Component({
  selector: 'register-hours',
  templateUrl: './register-hours.html',
  styleUrls: ['./register-hours.scss'],
})
export class RegisterHoursComponent extends BaseComponent implements OnInit {
  @Input() mode = 'UPDATE';
  horariosForm: FormGroup = new FormGroup({});
  diasSemana: string[] = ['Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado', 'Domingo'];
  private affiliationId = '';
  private organizationHours: HoursOfOperation[] = [];
  public submittedHours = false;
  public successSubmited = false;
  public canEdit = true;
  public affiliation: any;
  public specialtyChangeId: any;
  public actor = 'PRACTITIONER';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private injector: Injector,
    private dialogRef: MatDialogRef<RegisterHoursComponent>,
    private affiliationService: PractitionerOrganizationAffiliationService,
    private locationService: OrganizationLocationService,
    private fb: FormBuilder
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getFormValues();
    this.affiliationId = this.data.param.affiliation.affiliation_id;
    this.specialtyChangeId = this.data.param.specialtyChangeId;
    this.getAffiliation(this.affiliationId);
    if (this.data.param.actor) {
      this.actor = this.data.param.actor;
    }
  }

  getAffiliation(affiliation_id: any) {
    this.affiliationService.showByAffiliationId(affiliation_id).subscribe({
      next: (resAffiliation: any) => {
        this.affiliation = resAffiliation;
        const specialty = this.affiliation.specialty.find((spec: any) => spec.id === this.specialtyChangeId);
        if (specialty && specialty.practitionerHours) {
          this.setValuesToFormGroup(specialty.practitionerHours);
        } else {
          this.getFormValues();
        }
        this.getOrganizationHours(this.affiliation.subject.id);
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getFormValues() {
    this.diasSemana.forEach((dia) => {
      const hoursArray = this.fb.array([]);
      const dayFormGroup = this.fb.group({
        active: [false],
        hours: hoursArray,
      });
      this.horariosForm.addControl(dia, dayFormGroup);
      dayFormGroup.get('active')?.valueChanges.subscribe((active) => {
        if (!active) {
          hoursArray.clear();
        }
      });
    });
  }

  addHour(dia: string) {
    const formGroup = this.horariosForm.get(dia) as FormGroup;
    const hoursArray = this.getHoursArray(dia);
    if (hoursArray) {
      if (!formGroup.get('active')?.value) {
        formGroup.get('active')?.setValue(true);
      }
      hoursArray.push(
        this.fb.group({
          startHour: ['00:00', Validators.required],
          endHour: ['00:00', Validators.required],
        })
      );
    }
  }

  removeHour(dia: string, index: number) {
    const hoursArray = this.getHoursArray(dia);
    if (hoursArray) {
      hoursArray.removeAt(index);
      if (hoursArray.length === 0) {
        const dayFormGroup = this.horariosForm.get(dia) as FormGroup;
        dayFormGroup.get('active')?.setValue(false, { emitEvent: false });
      }
    }
  }

  setValuesToFormGroup(hoursOfOperation: DiaHorario[]) {
    hoursOfOperation.forEach((dayData: DiaHorario) => {
      const formGroup = this.horariosForm.get(dayData.dia) as FormGroup;
      if (formGroup) {
        formGroup.patchValue({
          active: dayData.active ?? false,
        });
        const hoursArray = formGroup.get('hours') as FormArray;
        hoursArray.clear();
        dayData.hours.forEach((hour) => {
          hoursArray.push(
            this.fb.group({
              startHour: [hour.startHour, Validators.required],
              endHour: [hour.endHour, Validators.required],
            })
          );
        });
      }
    });
  }

  getHoursArray(dia: string): FormArray | null {
    const formGroup = this.horariosForm.get(dia) as FormGroup;
    return formGroup?.get('hours') as FormArray;
  }

  onSubmit(): void {
    if (this.horariosForm.invalid) {
      this.horariosForm.markAllAsTouched();
      return;
    }
    const formValue = this.horariosForm.value;
    const practitionerHours = this.transformToPractitionerHours(formValue);
    if (!isAtLeastOneDayFilled(practitionerHours)) {
      this.openLongSnackBar('É necessário preencher pelo menos um dia com horário válido.', 'error');
      return;
    }
    const { invalidHours, validHours } = validatePractitionerHours(practitionerHours, this.organizationHours, this.getDayIndex);
    if (invalidHours.length > 0) {
      const invalidHoursMessage = `Dias e horários inválidos: ${invalidHours.join(', ')}`;
      const validHoursMessage = validHours.length > 0 ? `Horários permitidos: ${validHours.join(', ')}` : '';
      this.openLongSnackBar(`Horários fora do limite da organização. ${invalidHoursMessage}. ${validHoursMessage}`, 'error');
      return;
    }
    const existingRequests = Array.isArray(this.affiliation.changeRequest) ? this.affiliation.changeRequest : [];
    const hasPending = existingRequests.some(
      (r: any) => r.requestType === 'hoursChanges' && r.specialtyId === this.specialtyChangeId && r.status === 'pending'
    );
    if (hasPending && this.actor === 'PRACTITIONER') {
      this.openLongSnackBar('Já existe uma solicitação pendente para esta especialidade.', 'error');
      return;
    }
    const newRequest = {
      requestType: 'hoursChanges',
      changeRequestId: uuidv4(),
      status: this.actor === 'ORGANIZATION' ? 'accepted' : 'pending',
      requestedDate: moment().format(),
      practitionerHours: practitionerHours,
      specialtyId: this.specialtyChangeId,
    };
    if (this.actor == 'ORGANIZATION') {
      const updatedSpecialties = this.affiliation.specialty.map((specialty: any) => {
        if (specialty.id === this.specialtyChangeId && specialty.status === 'accepted') {
          return {
            ...specialty,
            practitionerHours: practitionerHours,
          };
        }
        return specialty;
      });
      const data = {
        ...this.affiliation,
        specialty: updatedSpecialties,
        changeRequest: [...existingRequests, newRequest],
      };
      this.save(data);
    } else {
      const data = {
        ...this.affiliation,
        changeRequest: [...existingRequests, newRequest],
      };
      this.save(data);
    }
  }

  private transformToPractitionerHours(formValue: any): DiaHorario[] {
    const practitionerHours: DiaHorario[] = [];
    this.diasSemana.forEach((dia) => {
      const dayData = formValue[dia];
      const hoursArray = dayData.hours.map((hour: any) => ({
        startHour: hour.startHour,
        endHour: hour.endHour,
      }));
      practitionerHours.push({
        dia,
        active: dayData.active,
        hours: hoursArray,
      });
    });
    return practitionerHours;
  }

  getOrganizationHours(organizationId: string) {
    this.locationService.showByOrganization(organizationId).subscribe({
      next: (resOrganization: any) => {
        this.organizationHours = resOrganization.hoursOfOperation;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getDayIndex(dia: string): number {
    const daysMap: { [key: string]: number } = {
      Domingo: 0,
      Segunda: 1,
      Terca: 2,
      Quarta: 3,
      Quinta: 4,
      Sexta: 5,
      Sabado: 6,
    };
    return daysMap[dia];
  }

  isWithinOrganizationHours(dia: string, hours: { startHour: string; endHour: string }[]): boolean {
    const dayIndex = this.getDayIndex(dia);
    const organizationDay = this.organizationHours.find((orgHour) => orgHour.dayOfWeek === dayIndex);
    if (!organizationDay || organizationDay.timeStart === '' || organizationDay.timeEnd === '') {
      return false;
    }
    const [orgStart, orgEnd] = [organizationDay.timeStart, organizationDay.timeEnd].map((time) => parseInt(time.replace(':', ''), 10));
    return hours.every(({ startHour, endHour }) => {
      const [startHourM, endHourM] = [startHour, endHour].map((time) => parseInt(time.replace(':', ''), 10));
      return startHourM >= orgStart && endHourM <= orgEnd;
    });
  }

  validateHours(control: AbstractControl): ValidationErrors | null {
    const hoursArray = control.get('hours') as FormArray;
    const dia = control.parent?.get('dia')?.value;
    if (hoursArray && dia) {
      for (const hourControl of hoursArray.controls) {
        const startHour = hourControl.get('startHour')?.value;
        const endHour = hourControl.get('endHour')?.value;
        if (startHour && endHour && !this.isWithinOrganizationHours(dia, [{ startHour, endHour }])) {
          return { invalidHours: true };
        }
      }
    }
    return null;
  }

  save(data: any) {
    this.affiliationService.update(this.affiliationId, data).subscribe({
      next: () => {
        this.submittedHours = true;
        this.successSubmited = true;

        this.openSnackBar("Solicitação de horário enviada com sucesso.", "success")
      
        this.dialogRef.close(true)
      },
      error: () => {
        this.submittedHours = true;
        this.successSubmited = false;
      },
    });
  }

  saveByOrganization(data: any) {
    this.affiliationService.update(this.affiliationId, data).subscribe({
      next: () => {
        this.submittedHours = true;
        this.successSubmited = true;

        this.openSnackBar("Solicitação de horário definida com sucesso.", "success")

        this.dialogRef.close(true)
      },
      error: () => {
        this.submittedHours = true;
        this.successSubmited = false;
      },
    });
  }
}
